// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import type TextField from '@mui/material/TextField';
import type { SlotComponentProps } from '@mui/material/utils';
import type { SxProps } from '@mui/system';
import type { DateValidationError } from '@mui/x-date-pickers';
import type dayjs from 'dayjs';
import * as React from 'react';
import { useUserContext } from '../../../../src/context/UserContext';
import { DatePicker } from '../../../lib/base';

interface Props {
  readonly value: dayjs.Dayjs | null;
  readonly onChange: (val: dayjs.Dayjs | null) => void;
  readonly label?: string;
  readonly minDate?: dayjs.Dayjs;
  readonly maxDate?: dayjs.Dayjs;
  readonly onError?: (newError: DateValidationError) => void;
  readonly disabled?: boolean;
  // biome-ignore lint/complexity/noBannedTypes: <explanation>
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  readonly textFieldProps?: SlotComponentProps<typeof TextField, {}, Record<string, any>>;
  readonly formatDensity?: 'dense' | 'spacious';
  readonly sx?: SxProps;
  readonly noClear?: boolean;
}

export default function InternalDatePicker({
  value,
  onChange,
  minDate,
  maxDate,
  label,
  textFieldProps,
  onError,
  disabled,
  formatDensity,
  sx,
  noClear,
}: Props) {
  const { preferredDateFormat } = useUserContext();

  return (
    <DatePicker
      // This is needed as of MUI X v8 to make input label shrink: true work
      enableAccessibleFieldDOMStructure={false}
      sx={sx}
      formatDensity={formatDensity}
      minDate={minDate}
      maxDate={maxDate}
      label={label}
      value={value}
      onChange={onChange}
      disabled={disabled}
      onError={onError}
      format={preferredDateFormat}
      closeOnSelect
      slotProps={{
        actionBar: {
          actions: noClear ? ['today'] : ['today', 'clear'],
        },
        textField: textFieldProps,
      }}
    />
  );
}
